/* eslint valid-typeof: off */
import { aporte } from '@constants/URL'
import con from '@services/connection'
import { Meses, TIPOS_RV, BENCHMARKS } from '@constants/Text'
import { BENCHMARK_PERSONALIZADO_COLOR } from '@constants/Color'
import { MASK_BRL, MASK_USD } from '@modules/global/constants/Currencies'
import { aawzDate as moment } from '@utils/dates/aawz-date'
import { textOnColor } from '@modules/global/utils/Color'

/**
 * Aplicar mascara monetaria para valor numérico.
 * Arredonda as casas decimais para a precisão.
 * @param {int} n Numero para formatar
 * @param {int} c Precisão das casas decimais
 * @param {String} d Separador decimal
 * @param {String} t Separador milhar
 */
export const formatNumber = (n, c = 2, d = ',', t = '.') => {
  c = isNaN((c = Math.abs(c))) ? 2 : c
  d = d === undefined ? '.' : d
  t = t === undefined ? ',' : t
  let s = n < 0 ? '-' : ''
  let i = String(
    parseInt((n = Math.abs(Number(isNaN(n) ? 0 : n) || 0).toFixed(c)))
  )
  let j = i.length > 3 ? i.length % 3 : 0
  return (
    s +
    (j ? i.substr(0, j) + t : '') +
    i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${t}`) +
    (c
      ? d +
        Math.abs(n - i)
          .toFixed(c)
          .slice(2)
      : '')
  )
}

/**
 * Formatar o valor numérico para string, removendo os zeros após a virgula
 */
export const formatNumberZero = (n, c = 2, d = ',', t = '.') => {
  let [inteiro, decimal] = formatNumber(n, c, d, t).split(d)
  decimal = (decimal || '').replace(/\,?0+$/, '')
  if (decimal && decimal.length) {
    return inteiro + d + decimal
  } else {
    return inteiro === '-0' ? '0' : inteiro
  }
}

/**
 * Converter número de string da mascara BR para valor tipo numério js.
 * @param {String} num Número a ser formatado. Esperada mascara BR de número.
 * @returns {Number} Número extraido.
 */
export const getNumber = (num) => {
  if (!num) return num
  if (typeof num === 'string') {
    return (
      Number(
        num
          .replace(/[^0-9,.-]/g, '')
          .replace(/\./g, '')
          .replace(',', '.')
      ) || 0
    )
  }
  return num
}

/**
 * Remover os pontos separadores de milhar, mantendo apenas a virgula como
 * separador decimal.
 * @param {String} val Representacao numerica para valor monetario
 * @returns Valor sem os pontos (somente virgula)
 */
export const removeDot = (val) => {
  if (val.includes('.')) {
    const tax = val.split('.')
    if (tax[1].includes(',')) {
      val = val.split('.').join('')
    }
  }
  return val
}

/**
 * Capitalizar texto para deixar somente a primeira letraa de cada palavra em caixa alta
 * @param {String} phrase Frase para capitalizar primeira letra de cada palavra
 */
export const toTitleCase = (phrase) => {
  if (!phrase || !phrase.toString) {
    return ''
  }
  return phrase
    .toString()
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Formata uma string em snake_case para um label capitalizado.
 * @param {String} text Texto a ser formatado.
 * @returns {String} Texto formatado para label.
 */
export const formatSnakeLabel = (text) => {
  if (!text || typeof text !== 'string') return text

  const formatted = text.replace(/_/g, ' ').toLowerCase()
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

/**
 * Adiciona a quantidade de dias passados.
 * @param {Date} data Data para ser considerada como base. Formato deve ser válido para o new Date().
 * @param {Number} days Número de dias à adicionar. Negativo ou positivo.
 * @returns {Date} Data calculada - Incluido os dias passados no parametro.
 */
export const addDays = (data, days) => {
  const date = new Date(data)
  date.setDate(date.getDate() + days)
  return date
}

/**
 * Realiza o cálculo do atraso da data passada em relação a data atual.
 * @param {Date} data_att Data no formato DD/MM/YYYY HH:mm.
 * @returns {Number} Resultado da comparação:
 *          1 - hoje
 *          2 - Até 4 dias desatualizado
 *          3 - Até 8 dias desatualizado
 *          4 - > 8 dias desatualizado
 */
export const statusDesatualizado = (data_att, range = 3) => {
  if (typeof data_att !== 'string' || !data_att) {
    return 0
  }
  let mask = 'DD/MM/YYYY'
  if (data_att.includes(':')) {
    const timePart = data_att.split(' ')[1] || ''
    mask =
      timePart.split(':').length >= 3
        ? 'DD/MM/YYYY HH:mm:ss'
        : 'DD/MM/YYYY HH:mm'
  }
  const actualDate = moment()
  const diff = actualDate.diff(moment(data_att, mask), 'days')
  if (diff <= 0) {
    return 1
  }
  if (diff <= 6) {
    return 2
  }
  if (diff <= 9) {
    return 3
  }
  return 4
}

/**
 * Formatar para percentual conforme nosso padrão.
 * @param {Number} num Percentual em valor decimal (55.5).
 * @param {Number} precision Quantidade de casas decimais.
 * @param {Number} ignore_zero Remover zeros e ponto decimal no final, caso tenha
 * @return {String} Percentual formatado (55,5%);
 */
export const toPercent = (num, precision = 2, ignore_zero = false) => {
  if (typeof num === 'number' || Number(num)) {
    let formattedNumber = formatNumber(num, precision)
    if (ignore_zero) {
      // Remover zeros finais e ponto decimal, se necessário
      formattedNumber = formattedNumber.replace(/\,?0+$/, '')
    }
    return `${formattedNumber}%`
  }
  return num
}

/**
 * Calcula e atribui percentual_grupo para cada item de um grupo/cluster.
 * @param {Array} items - Array de ativos/itens do grupo
 * @param {String} plField - Nome do campo que contém o valor do PL (default: 'pl')
 * @returns {Array} - O mesmo array com percentual_grupo calculado
 */
export const calcularPercentualGrupo = (items, plField = 'pl') => {
  const total = items.reduce((sum, item) => sum + getNumber(item[plField]), 0)

  items.forEach((item) => {
    const rawValue = total > 0 ? getNumber(item[plField]) / total : 0
    item.percentual_grupo_raw = rawValue
    item.percentual_grupo = toPercent(rawValue * 100)
  })

  return items
}

/**
 * Aplica máscara monetária de reais.
 * @param {Number} num Valor em reais.
 * @param {Number} precision Precisao das casas decimais.
 * @param {String} currency Qual moeda será utilizada (real ou dol).
 * @returns {String} Valor formatado (R$ 222,22).
 */
export const toMoney = (num, precision = 2, currency = 'real') => {
  if (currency === 'dol') {
    currency = MASK_USD
  }
  if (currency === 'real') {
    currency = MASK_BRL
  }
  if (typeof num === 'number' || Number(num)) {
    return `${currency} ${formatNumber(num, precision)}`
  }
  return num
}

/**
 * Verifica se o parametro recebido é um cnpj valido
 * @param {String} cnpj String para verificar
 * @returns {Boolean} Cnpj valido
 */
export const validateCnpj = (cnpj) => {
  const verify = /^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/
  return verify.test(cnpj)
}

/**
 * Considera se o objeto é um JSON, retorno o próprio objeto.
 * @param {String/Object} j Objeto a verificar.
 * @returns {Object} Objeto recebido.
 */
export const possivelJSON = (j) => {
  try {
    return typeof j === 'string' ? JSON.parse(j) : j
  } catch (e) {
    return j
  }
}

/**
 * Fazer parse de JSON tolerando valores inválidos (NaN/Infinity).
 * - Retorna o próprio input quando não for string.
 * - Substitui NaN, Infinity e -Infinity por null antes do parse.
 * - Em falha de parse, retorna null.
 * @param {any} input Valor a ser parseado
 * @returns {any|null} Objeto/array parseado, primitivo original ou null em erro
 */
export const safeJsonParse = (input) => {
  try {
    if (typeof input !== 'string') {
      return input
    }
    const sanitized = input
      .replace(/^\s*NaN\s*$/i, 'null')
      .replace(/^\s*-?Infinity\s*$/i, 'null')
      .replaceAll(/:\s*NaN/gi, ':null')
      .replaceAll(/:\s*-?Infinity/gi, ':null')
      .replaceAll(/([[,])\s*NaN/gi, '$1null')
      .replaceAll(/([[,])\s*-?Infinity/gi, '$1null')
      .replace(/^\s*NaT\s*$/i, 'null')
      .replaceAll(/:\s*NaT/gi, ':null')
      .replaceAll(/([[,])\s*NaT/gi, '$1null')
    return JSON.parse(sanitized)
  } catch {
    return null
  }
}

/**
 * @param {any} value
 * @returns {Array} value se já for array; [] se vazio (null/undefined/''); senão [value]
 */
export const coerceToArray = (value) => {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined || value === '') return []
  return [value]
}

/**
 * Recebe o endereço de e-mail e verifica o formato dele.
 * @param {String} email Endereço de e-mail.
 * @returns {Boolean} Endereço válido
 */
export const checkEmail = (email) => {
  // const chars = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  const chars =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return chars.test(email)
}

/**
 * Formata o telefone para o padrão BR, considerando a possibilidade de 10, 11, 12 ou 13 dígitos.
 * @param {String} phone Telefone a ser formatado.
 * @returns {String} Telefone formatado.
 */
export const formatPhone = (phone) => {
  phone = phone.replace(/\D/g, '')
  if (phone.length === 10) {
    return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  }
  if (phone.length === 11) {
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }
  if (phone.length === 12) {
    return phone.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '+$1 ($2) $3-$4')
  }
  if (phone.length === 13) {
    return phone.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4')
  }
  return phone
}

/**
 * Formatar o endereço de objeto para texto.
 * @param {Object} end Objeto com os atributos do endereco [rua, numero*, complemento*, bairro, cidade, estado, cep*]
 */
export const formatEndereco = (end) => {
  let txt = ''
  txt += end.rua
  if (end.numero) {
    txt += ', ' + end.numero
  }
  if (end.complemento) {
    txt += ' - ' + end.complemento
  }
  txt += `, ${end.bairro}, ${end.cidade}/${end.estado}`
  if (end.cep) {
    txt += ' - ' + end.cep
  }
  return txt
}

/**
 * Formata o valor do CPF.
 * @param {String} cpf CPF a ser formatado.
 * @returns {String} CPF formatado.
 */
export const formatCPF = (cpf) => {
  cpf = cpf.replace(/\D/g, '')
  cpf = cpf.replace(/(\d{3})(\d)/, '$1.$2')
  cpf = cpf.replace(/(\d{3})(\d)/, '$1.$2')
  cpf = cpf.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  return cpf
}

/**
 * Formata o valor do CNPJ.
 * @param {String} cnpj CNPJ a ser formatado.
 * @returns {String} CNPJ formatado.
 */
export const formatCNPJ = (cnpj, fill = false) => {
  const CNPJ_LENGTH = 14
  if (fill && cnpj.toString().length < CNPJ_LENGTH) {
    for (let i = 0; i < CNPJ_LENGTH - cnpj.toString().length; i++) {
      cnpj = '0' + cnpj
    }
  }

  if (typeof cnpj !== 'string') {
    cnpj = String(cnpj)
  }

  cnpj = cnpj.replace(/\D/g, '')
  // cnpj = cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g,"\$1.\$2.\$3\/\$4\-\$5");
  cnpj = cnpj.replace(/^(\d{2})(\d)/, '$1.$2')
  cnpj = cnpj.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
  cnpj = cnpj.replace(/\.(\d{3})(\d)/, '.$1/$2')
  cnpj = cnpj.replace(/(\d{4})(\d)/, '$1-$2')
  return cnpj
}

/**
 * Formatar o campo de documento de acordo com tamanho.
 * @param {String} text CPF ou CNPJ a ser formatado.
 * @returns {String} Campo de documento formatado de acordo com o tipo
 */
export const formatCPFeCNPJ = (text) => {
  const onlyNumbers = text.replace(/\D/g, '')
  if (onlyNumbers.length <= 11) {
    return formatCPF(text)
  }
  return formatCNPJ(text)
}

/**
 * Formata o valor do CEP.
 * @param {String} cep CEP a ser formatado.
 * @returns {String} CEP formatado.
 */
export const formatCEP = (cep) => {
  cep = cep.replace(/^(\d{5})(\d)/, '$1-$2')
  return cep
}

/**
 * Retornar apenas os numeros de um texto.
 * @param {String} v String a ser formatada.
 */
export const extractNumber = (v) => {
  if (v) {
    return v.replace(/[^0-9]/g, '')
  }
  return v
}

/**
 * Formatar data para o formato válido para a Wirecard.
 * @param {Date} data Data no formato válido para o moments.
 * @returns {String} Data formatada (YYYY-MM-DD).
 */
export const dataWirecard = (data) =>
  moment(data, 'DD/MM/YYYY').format('YYYY-MM-DD')

/**
 * Formatar data do formato da Wirecard para nosso padrão.
 * @param {Date} data Data no formato da Wirecard.
 * @returns {String} Data formatada (DD/MM/YYYY).
 */
export const dataWirecardReverse = (data) =>
  moment(data, 'YYYY-MM-DD').format('DD/MM/YYYY')

/**
 * Formata um valor para data
 * @param {String} data Valor que deseja transformar em data
 * @returns {String} Retorna o valor em formato como data
 */
export const toDate = (data) => {
  data = data.replace(/\D/g, '')
  data = data.replace(/(\d{2})(\d)/, '$1/$2')
  data = data.replace(/(\d{2})(\d)/, '$1/$2')
  data = data.replace(/(\d{2})(\d{2})$/, '$1$2')

  return data
}

/**
 * Formatar data com mẽs escrito, para utilizar nas legendas.
 * @param {Date} date Data base.
 * @returns {String} Data formatada (MMM/YY)
 */
export const toMonthYear = (date, fullYear = false, dateFormat = false) => {
  if (typeof date === 'date' || (date.getFullYear && date.getMonth)) {
    if (fullYear) {
      return `${Meses[date.getMonth()]}/${date.getFullYear().toString()}`
    } else {
      return `${Meses[date.getMonth()]}/${date
        .getFullYear()
        .toString()
        .replace('20', '')}` // TODO Outra maneira de coletar somente o final
    }
  }
  if (dateFormat) {
    return `${Meses[Number(date.split('/')[0]) - 1]}/${date.split('/')[1]}`
  }
  return date
}

export const toMonthYearMoment = (
  data,
  mask = 'YYYY-MM-DD',
  format = 'MMM/YY'
) => {
  return moment(data, mask).format(format)
}

/**
 * Validar data.
 * @param {String} d Data a ser validada.
 * @param {String} mask Mascara a ser utilizada. DD/MM/YYYY
 */
export const validarData = (d, mask = 'DD/MM/YYYY', norequired) => {
  if (d) {
    if (d.length === mask.length) {
      const data = moment(d, mask).format(mask) // TODO mascara de data constant
      return data === 'Invalid date' ? false : data
    }
  }
  return !!norequired
}

/**
 * Comparando duas datas simples.
 * 	 1 - Primeira data maior.
 *   0 - Datas iguais.
 * 	-1 - Primeira data menor.
 * @param {String} d1 Primeira data para comparação (DD/MM/YYYY).
 * @param {String} d2 Segunda data para comparação (DD/MM/YYYY).
 */
export const compararDatas = (d1, d2 = moment().format('DD/MM/YYYY')) => {
  if (d2 && typeof d2 === 'string') {
    d2 = moment(d2, 'DD/MM/YYYY')
  }
  if (d1) {
    let d1F = moment(d1, 'DD/MM/YYYY')
    return Math.sign(d1F - d2)
  }
  return null
}

/**
 * Formatar data com o padrão do sistema.
 * @param {Date|String} d Campo da data a ser formatado.
 * @param {String} tipo Qual tipo de data esperado na resposta (Completo(DD/MM/YYYY HH:mm)|Curto(DD/MM/YYYY)|Abreviado(DD/MM/YY)).
 * @param {String} mask_in Mascara da data a ser formatada.
 */
export const formatarData = (d, tipo = 'Completo', mask_in = 'DD/MM/YYYY') => {
  if (tipo === 'Completo') {
    return formatDate(d, mask_in, 'DD/MM/YYYY HH:mm')
  } else if (tipo === 'Curto') {
    return formatDate(d, mask_in, 'DD/MM/YYYY')
  } else if (tipo === 'Abreviado') {
    return formatDate(d, mask_in, 'DD/MM/YY')
  } else {
    return formatDate(d, mask_in, 'DD/MM/YYYY')
  }
}

/**
 * Formatar data de acordo com a mascara sendo passada no parametro.
 * @param {Date|String} d Valor da data a ser formatado.
 * @param {String} mask_in Formato da data a ser formatada caso string.
 * @param {String} mask_out Formato da data de retorno.
 */
export const formatDate = (
  d,
  mask_in = 'DD/MM/YYYY',
  mask_out = 'DD/MM/YYYY'
) => {
  if (d) {
    let data = '-'
    try {
      if (typeof d === 'string' && d.length === 10) {
        data = moment(d, mask_in).format(mask_out)
      } else {
        data = moment(d).format(mask_out)
      }
      return data === 'Invalid date' ? '-' : data
    } catch (err) {
      console.info(err)
      return '-'
    }
  }
  return '-'
}

/**
 *
 * @param {String} d em datetime ('Thu, 24 Oct 2019 00:00:00 GMT')
 * @param {Number} hours_to_add
 * @param {String} format formato de retorno
 */
export const datetimeToDate = (d, hours_to_add = 0, format = 'DD/MM/YYYY') => {
  return moment(d).add(hours_to_add, 'hours').format(format)
}

export const fileSize = (size) => {
  const i = Math.floor(Math.log(size) / Math.log(1024))
  return (
    (size / Math.pow(1024, i)).toFixed(2) * 1 +
    ' ' +
    ['B', 'kB', 'MB', 'GB', 'TB'][i]
  )
}

export const getCorretora = (v) => {
  // return corretoras[v] || corretorasReverse[v] || v
  return v
}

export const getCorretoraSimple = (v) => {
  // return corretoras[v] || v
  return v
}

const computeAplicarCotizacaoDate = (data, tipoAtivo) => {
  const parsed = moment(data, 'DD/MM/YYYY')
  if (!parsed.isValid()) return null

  let daysToAdd = 1
  if (tipoAtivo && TIPOS_RV.includes(tipoAtivo)) {
    daysToAdd = 0
  }
  const isFundo = ['Fundos de Investimentos', 'Fundo', 'Fundos'].includes(
    tipoAtivo
  )
  const isRF = tipoAtivo === 'Renda Fixa'
  const isTesouro = tipoAtivo === 'Tesouro Direto'
  if (tipoAtivo && !(isFundo || isRF || isTesouro)) {
    daysToAdd = 0
  }
  return parsed.add(daysToAdd, 'days').format('DD/MM/YYYY')
}

const fetchResgatarCotizacaoDate = async (data, cnpj) => {
  if (!cnpj) {
    // Verificar se realmente é um cnpj
    return
  }
  const dados = {
    data_aplicacao: data,
    CNPJ: cnpj,
  }
  const r = await con.post(aporte.data_cotizacao, dados)
  if (r.status === 200) {
    return r.value.data_cotizacao
  }
}

export const getDataCotizacao = async (data, method, cnpj, tipoAtivo) => {
  if (!(data && method)) {
    return
  }

  if (method === 'Aplicar' || !cnpj) {
    return computeAplicarCotizacaoDate(data, tipoAtivo)
  }

  if (method === 'Resgatar') {
    const result = await fetchResgatarCotizacaoDate(data, cnpj)
    return result
  }
}

/**
 * Remover objetos duplicados da lista, considerando atributo selecionado
 * @param {Array} arr Lista a ser filtrada
 * @param {String} comp Valor usado para comparação
 */
export const getUnique = (arr, comp) => {
  // store the comparison  values in array
  const unique = arr
    .map((e) => (comp ? e[comp] : e))

    // store the indexes of the unique objects
    .map((e, i, final) => final.indexOf(e) === i && i)

    // eliminate the false indexes & return unique objects
    .filter((e) => arr[e])
    .map((e) => arr[e])

  return unique
}

/**
 * Abrevia um valor
 * @param {Number} valor valor a ser abreviado
 * @param {Number} precisao O número de casas decimais (opcional, padrão é 1)
 * @param {Boolean} textoCompleto Define se deve retornar o valor com texto completo ou o sufixo
 * @returns {String} O valor com texto (sufixo ou completo)
 */
export const abreviarValor = (valor, precision = 1, textoCompleto = false) => {
  // if (Math.abs(valor) >= 1000000000) {
  //   return (valor / 1000000000).toFixed(1).replace('.0', '') + 'B'
  // }
  // if (Math.abs(valor) >= 1000000 && Math.abs(valor) < 1000000000) {
  //   return (valor / 1000000).toFixed(1).replace('.0', '') + 'M'
  // }
  // if (Math.abs(valor) >= 1000 && valor < 1000000) {
  //   return (valor / 1000).toFixed(1).replace('.0', '') + 'K'
  // }
  const zeros = `,${'0'.repeat(precision)}`
  if (Math.abs(valor) >= 1000000000) {
    const valorAbreviado = formatNumber(valor / 1000000000, precision).replace(
      zeros,
      ''
    )

    return textoCompleto
      ? `${valorAbreviado} ${pluralize(valorAbreviado, 'bilhão', 'bilhões')}`
      : `${valorAbreviado} B`
  }
  if (Math.abs(valor) >= 1000000 && Math.abs(valor) < 1000000000) {
    const valorAbreviado = formatNumber(valor / 1000000, precision).replace(
      zeros,
      ''
    )

    return textoCompleto
      ? `${valorAbreviado} ${pluralize(valorAbreviado, 'milhão', 'milhões')}`
      : `${valorAbreviado} M`
  }
  if (Math.abs(valor) >= 1000 && Math.abs(valor) < 1000000) {
    const valorAbreviado = formatNumber(valor / 1000, precision).replace(
      zeros,
      ''
    )

    return textoCompleto ? `${valorAbreviado} mil` : `${valorAbreviado} K`
  }

  return formatNumber(valor, precision)
}

/**
 * Abreviar um text com mascara monetaria
 * @param {Number} v Valor a ser abreviado
 * @returns {String} Valor monetario abreviado
 */
export const abreviarMonetario = (value, precision = -2, currency = 'real') => {
  if (!value) {
    return '-'
  }

  const normalizedPrecision = typeof precision === 'number' ? precision : -2

  let currencyMask = currency

  if (currency === 'dol') {
    currencyMask = MASK_USD
  } else if (currency === 'real') {
    currencyMask = MASK_BRL
  }

  const roundedValue = Math.round10(value, normalizedPrecision)
  const abbreviatedValue = abreviarValor(roundedValue)

  return `${currencyMask} ${abbreviatedValue}`
}

/**
 * Separar o valor da taxa em indexador e taxa.
 * @param {String} taxa Valor completo da taxa. Ex:
 *
 * @returns {Object} {indexador:str, taxa:number}
 */
// https://regexr.com/6ahck
const TAX_INDEX_STRING = 'TAXA'
const TAX_NUMERIC_REGEX = /(\d*[.|,]?\d+)*[.|,]?\d+/g
const TAX_AMERICAN_INDEX_REGEX = /(\d*,?\d+)*[.]?\d+/g
const TAX_BRAZILIAN_INDEX_REGEX = /(\d*[.]?\d+)*,?\d+/g

const formatAmericanTax = (numericPart, normalizedTaxa) => {
  return {
    formato: 'us',
    taxa: getNumber(numericPart.split(',').join('').replace('.', ',')) || 0,
    indexador: normalizedTaxa
      .replaceAll(TAX_AMERICAN_INDEX_REGEX, TAX_INDEX_STRING)
      .replace('%', ''),
  }
}

const formatBrazilianTax = (numericPart, normalizedTaxa) => {
  const hasNumber = normalizedTaxa.match(TAX_NUMERIC_REGEX)
  const indexador = normalizedTaxa
    .replaceAll(TAX_BRAZILIAN_INDEX_REGEX, TAX_INDEX_STRING)
    .replace('%', '')
  return {
    formato: 'br',
    taxa: getNumber(numericPart),
    indexador: hasNumber ? indexador : 'TAXA ' + indexador,
  }
}

const extractTaxNumericPart = (normalizedTaxa) => {
  const matches = normalizedTaxa.match(TAX_NUMERIC_REGEX)
  if (!matches) {
    return '0'
  }
  return matches[0].replace('%', '')
}

const isBrazilianDotFormat = (numericPart) => {
  const splitPonto = numericPart.split('.')
  // Formato brasileiro: milhar com 3 dígitos após o ponto e inteiro > 0,
  // ou vírgula decimal presente após o ponto
  return (
    (splitPonto[1].length === 3 && splitPonto[0] > 0) ||
    splitPonto[1].includes(',')
  )
}

const isAmericanCommaFormat = (numericPart) => {
  const splitVirgula = numericPart.split(',')
  return splitVirgula[1].includes('.')
}

export const splitTax = (taxa) => {
  if (!taxa || typeof taxa !== 'string') {
    return {
      indexador: '',
      taxa: '',
      formato: 'none',
    }
  }

  const normalizedTaxa = taxa.replace('IPCA', 'IPC-A')
  const numericPart = extractTaxNumericPart(normalizedTaxa)

  // 108.5 4.45 2.32 120 0.035 0.2
  // 108,5 4,45 2,32 120 0,035 0,2

  if (numericPart.includes('.')) {
    return isBrazilianDotFormat(numericPart)
      ? formatBrazilianTax(numericPart, normalizedTaxa)
      : formatAmericanTax(numericPart, normalizedTaxa)
  }

  if (numericPart.includes(',')) {
    return isAmericanCommaFormat(numericPart)
      ? formatAmericanTax(numericPart, normalizedTaxa)
      : formatBrazilianTax(numericPart, normalizedTaxa)
  }

  return formatBrazilianTax(numericPart, normalizedTaxa)
}

/**
 * Recuperar apenas as iniciais do primeiro e último nome
 * @param {String} nome Nome a ser analisado
 */
export const getInitials = (nome) => {
  const cleaned = String(nome || '')
    .replaceAll(/['"]/g, '')
    .trim()
  const names = cleaned.split(' ').filter(Boolean)
  if (!names.length) return ''
  let initials = names[0].substring(0, 1).toUpperCase()

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase()
  }
  return initials.toUpperCase()
}

/**
 * Formata uma duração em segundos como relógio (HH:MM:SS ou MM:SS).
 * @param {Number} totalSeconds Total de segundos.
 * @param {Object} [options]
 * @param {Boolean} [options.alwaysShowHours=true] Mantém o campo de horas mesmo quando zero.
 * @returns {String} Duração formatada (ex.: '01:23:45' ou '23:45').
 */
export const formatSecondsToDuration = (
  totalSeconds,
  { alwaysShowHours = true } = {}
) => {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  if (!alwaysShowHours && hours === 0) {
    return `${pad(minutes)}:${pad(seconds)}`
  }
  const hoursLabel = alwaysShowHours ? pad(hours) : String(hours)
  return `${hoursLabel}:${pad(minutes)}:${pad(seconds)}`
}

const MS_PER_SECOND = 1000
const SECONDS_PER_HOUR = 3600

/**
 * Formata milissegundos decorridos (ex.: timestamp de segmento de transcrição)
 * como duração (mm:ss, ou HH:mm:ss a partir de 1h). Wrapper fino sobre
 * formatSecondsToDuration — usar sempre que o valor de origem for elapsed-ms
 * em vez de segundos, para não duplicar a conversão em cada consumidor.
 * @param {Number} elapsedMs Milissegundos decorridos.
 * @returns {String} Duração formatada, ou '' se ausente.
 */
export const formatElapsedMsToDuration = (elapsedMs) => {
  if (elapsedMs === null || elapsedMs === undefined) return ''
  const totalSeconds = Math.floor(Number(elapsedMs) / MS_PER_SECOND)
  const alwaysShowHours = totalSeconds >= SECONDS_PER_HOUR
  return formatSecondsToDuration(totalSeconds, { alwaysShowHours })
}

// Formato em que o backend serializa datetimes de transcrição (day-first).
const BACKEND_DATETIME_FORMAT = 'DD/MM/YYYY HH:mm'

/**
 * Faz o parse de um datetime serializado pelo backend no formato day-first
 * ('DD/MM/YYYY HH:mm') usando aawzDate.
 * @param {String} value Datetime serializado.
 * @returns {object|null} Instância aawzDate válida, ou null se ausente/inválido.
 */
export const parseBackendDateTime = (value) => {
  if (!value) return null
  const parsed = moment(value, BACKEND_DATETIME_FORMAT)
  return parsed && parsed.isValid() ? parsed : null
}

/**
 * Formata um datetime do backend (day-first) de volta como 'DD/MM/YYYY HH:mm'.
 * @param {String} value Datetime serializado.
 * @param {String} [fallback='-'] Retorno quando ausente/inválido.
 * @returns {String} Datetime formatado ou o fallback.
 */
export const formatBackendDateTime = (value, fallback = '-') => {
  const parsed = parseBackendDateTime(value)
  return parsed ? parsed.format(BACKEND_DATETIME_FORMAT) : fallback
}

/**
 * Duração em segundos entre dois datetimes do backend (day-first).
 * @param {String} start Datetime inicial.
 * @param {String} end Datetime final.
 * @returns {Number|null} Segundos inteiros (>= 0), ou null se ausente/inválido/negativo.
 */
export const backendDurationSeconds = (start, end) => {
  const startDate = parseBackendDateTime(start)
  const endDate = parseBackendDateTime(end)
  if (!startDate || !endDate) return null
  const diffMs = endDate.diff(startDate)
  if (diffMs < 0) return null
  return Math.floor(diffMs / 1000)
}

/**
 * Remove os acentos do texto.
 * @param {String} texto Texto a ser considerado.
 * @returns {String} Texto tratado.
 */
export const removeAccents = (texto) => {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Normalizar texto, removendo caracteres diferentes, acentos, espaçãos ao redor e colocando pra lower case.
 * @param text
 */
export const normalizeText = (text) => {
  return removeAccents(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

/**
 * Object com partes do valor rgb
 * @param {String} hex Cor no formato hexadecimal
 * @returns
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

/**
 * Format hex color to rbg with opacity.
 * @param {String} hex Color at hexadecimal format
 * @returns {String} rbg at color with opacity
 */
export const getColorWithOpacity = (hex, opacity = 0.5) => {
  const color = hexToRgb(hex.toLowerCase())
  return color ? `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})` : hex
}

/**
 * Converts a hex color ("#56AF79" / "56af79" / "#abc") to an "r, g, b" string,
 * useful to build `rgba(<rgb>, <alpha>)` values. Tolerant: non-hex inputs
 * (design-system token names like `secondary`, `var(--x)`, malformed values)
 * fall back to `fallback` instead of throwing.
 * @param {String} hex Color at hexadecimal format (6-digit or 3-digit shorthand)
 * @param {String} fallback Value returned when `hex` is not a parseable hex color
 * @returns {String} "r, g, b" string
 */
export const hexToRgbString = (hex, fallback = '142, 142, 142') => {
  if (typeof hex !== 'string') return fallback
  let h = hex.trim()
  if (h.startsWith('#')) h = h.slice(1)
  // Expande shorthand (#abc → #aabbcc) antes de delegar ao hexToRgb,
  // que só aceita a forma de 6 dígitos.
  if (h.length === 3) {
    h =
      h.charAt(0) +
      h.charAt(0) +
      h.charAt(1) +
      h.charAt(1) +
      h.charAt(2) +
      h.charAt(2)
  }
  const rgb = hexToRgb(h)
  return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : fallback
}

/**
 * Metodo para retornar somente o ano da data.
 * @param {String} data Valor da data completo
 * @param {String} mask [Optional] Valor da máscara da data original. Valor aceito pelo moment.
 * @return Ano da data
 */
export const getYear = (data, mask = 'YYYY-MM-DD') => {
  return moment(data, mask).format('YYYY')
}

/**
 * Metodo para retornar o mes por escrito da data.
 * @param {String} data Valor da data completo
 * @param {String} mask [Optional] Valor da máscara da data original. Valor aceito pelo moment.
 * @return Mês da data por escrito
 */
export const getMonth = (data, mask = 'YYYY-MM-DD') => {
  return moment(data, mask).format('MMM')
}

/**
 * Comparar dois objetos diferentes.
 * @param {Object} a Objeto para comparacao
 * @param {Object} b Objeto para comparacao
 * @returns {Boolean} Se os objetos sao iguais
 */
export const compareObjects = (a, b) => {
  const keys1 = Object.keys(a).filter((k) => {
    return k !== 'index'
  })
  const keys2 = Object.keys(b).filter((k) => {
    return k !== 'index'
  })

  if (keys1.length !== keys2.length) {
    return false
  }

  for (let key of keys1) {
    if (a[key] !== b[key]) {
      return false
    }
  }

  return true
}

/**
 * Verificar se ja passou certo tempo da data
 * @param {Date} date Data base para comparacao
 * @param {Number} min Tempo minimo considerado
 * @returns {Boolean} Passou mais de <min> minutos de <date>
 */
export const diffTimeMinutes = (date, min) => {
  if (!date) {
    return true
  }
  return new Date() - date >= min * 60000
}

/**
 * Retorna o valor percentual formatado a partir de um valor decimal.
 * Ex: 0.1 -> 10,00%
 */
export const toDecPercent = (v, precision) => {
  return toPercent(v * 100, precision)
}

/**
 * Gerar uma cor aleatória.
 * @returns {String} Cor Hexadecimal
 */
export const getRandomColor = () => {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

/**
 * Gerar uma lista de valores aleatorios.
 * @param {Number} range Maximo para os valores
 * @param {Number} size Tamanho da lista
 * @returns {Array} Lista de valores aleatorios
 */
export const getRandomList = (range, size) => {
  const aux = []
  for (let i = 0; i < size; i++) {
    aux.push((Math.random() * range).toFixed(2))
  }
  return aux
}

export const pluralize = (val, singular, plural) => {
  return Math.trunc(Math.abs(getNumber(val))) === 1 ? singular : plural
}

// 0 -> prazo certo; 1 -> do mês passado; 2-> mais de um mês de atraso.
export const checkDelay = ({ data_atualizacao, dataAtual } = {}) => {
  if (!data_atualizacao) {
    return ''
  }
  const hoje = dataAtual ? moment(dataAtual, 'DD/MM/YYYY') : moment()
  const data = moment(data_atualizacao, 'DD/MM/YYYY')
  if (hoje.diff(data, 'months') > 0) {
    return 2
  } else {
    if (hoje.month() === data.month()) {
      return 0
    } else {
      return 1
    }
  }
}

/**
 * Lista para formatar os dados do gráfico de linha com benchmarks
 * @param {Object} r {value: {graph: {benchmark:{...}}}}
 * @returns {Array} Lista com objetos formatados
 */
// Paleta de cores para benchmarks nomeados (legacy + dinâmicos)
const _BENCHMARK_COLORS = {
  CDI: '#2D212F',
  'IPC-A': '#FF5722',
  'IMA-B': '#388E3C',
  IBOVESPA: '#FFA000',
  IBOV: '#FFA000',
  IFIX: '#644D40',
  'IRF-M': '#AAABE2',
  DÓLAR: '#2E86AB',
  DOLAR: '#2E86AB',
  'S&P 500': '#004D40',
  CPI: '#8E24AA',
  // Benchmark personalizado do cliente — cor institucional AAWZ ouro
  cliente_benchmark: BENCHMARK_PERSONALIZADO_COLOR,
}

const _makeBenchmarkLine = (label, color, data) => ({
  label,
  fill: false,
  stepped: true,
  borderWidth: 3,
  borderColor: color,
  backgroundColor: color,
  data: data || [],
  line: { borderDash: [0, 10], borderCapStyle: 'round' },
})

export const setLineBenchmarks = (r, clienteBenchmarkMeta) => {
  const benchmarkData =
    (r.value && r.value.graph && r.value.graph.benchmark) || {}

  // Benchmarks fixos (ordem de exibição) — reutiliza a lista canônica de @constants/Text
  const lines = BENCHMARKS.filter(
    (key) => benchmarkData[key] && benchmarkData[key].length
  ).map((key) =>
    _makeBenchmarkLine(
      key,
      _BENCHMARK_COLORS[key] || '#888888',
      benchmarkData[key]
    )
  )

  // Benchmark personalizado do cliente — vai primeiro se existir e não for CDI puro
  if (
    benchmarkData['cliente_benchmark'] &&
    benchmarkData['cliente_benchmark'].length
  ) {
    const label =
      (clienteBenchmarkMeta && clienteBenchmarkMeta.label) || 'Meu Benchmark'
    // Evita duplicata visual: se label == 'CDI' ou '100% CDI' e CDI já está na lista, não duplica
    const isCdiDuplicate =
      (label === 'CDI' || label === '100% CDI') &&
      lines.some((l) => l.label === 'CDI')
    if (!isCdiDuplicate) {
      const clienteLine = _makeBenchmarkLine(
        label,
        BENCHMARK_PERSONALIZADO_COLOR,
        benchmarkData['cliente_benchmark']
      )
      lines.unshift(clienteLine) // exibe primeiro
    }
  }

  return lines
}

/**
 * Verificar se o objeto tem algum valor zerado (para apresentacao de aviso)
 * @param {Object} item Item de financeiro unico
 * @returns
 */
export const checkUniqueFinanceNull = (item) => {
  return [
    'carteira',
    'roa_liquido',
    'luvas_por_ano',
    'bonificacao',
    'custo_fixo_em_porcentagem_receita',
  ].some((k) => {
    return !item[k]
  })
}
export const checkConversionNull = (item) => {
  return ['conversao_ano_1', 'conversao_ano_2', 'conversao_ano_3'].some((k) => {
    return !item[k]
  })
}
export const checkComissionNull = (item) => {
  return [
    'comissao_ano_2',
    'comissao_ano_3',
    'comissao_anos_subsequentes',
  ].some((k) => {
    return !item[k]
  })
}

export const negativeToParentheses = (
  value,
  { suffix = '', precision = 0 } = {}
) => {
  if (!value || Number.isNaN(Number(value))) {
    return value || '-'
  }
  const val = getNumber(value)
  return val < 0
    ? `(${suffix || ''}${formatNumber(val, precision).replace('-', '')})`
    : (suffix || '') + formatNumber(value, precision) || ''
}

/**
 * Formatar um objeto em query string para requisições GET
 * @param object Parametros necessários
 * @param invalidValues Valores a serem ignorados
 * @returns {string} Query params completo, basta adicionar após a URL
 */
export const formatQueryParams = (
  object,
  invalidValues = [undefined, null]
) => {
  const aux = []
  Object.keys(object).forEach((key) => {
    if (!invalidValues.includes(object[key])) {
      aux.push(`${key}=${encodeURIComponent(object[key])}`)
    }
  })
  return '?' + aux.join('&')
}

/**
 * Formatar um nome para mostrar primeiro nome completo e segundo completo e terceiro apenas a primeira letra
 * @param name Parametros necessários
 * @returns {string} nome formatado
 */
export const formatName = (name) => {
  if (typeof name === 'string') {
    const words = name.split(' ')

    if (words.length > 0) {
      let shortenedName = words[0]

      if (words.length > 2) {
        shortenedName +=
          ' ' + words[1].charAt(0) + ' ' + words[2].charAt(0) + '.'
      }

      return shortenedName
    }
  }
  return name
}

/**
 * Formatar uma string removendo todos os ./-!
 * criado inicialmente para deixar somente os alfanumérico de uma cnpj
 * @param value Parametros necessários
 * @returns {string} value formatado
 */
export const removeSymbols = (value) => {
  return value.replace(/[^a-zA-Z0-9]/g, '')
}

/**
 * Formatar um nome para mostrar o primeiro e o último nome
 * Caso tenha apenas um nome, retorna ele.
 * Se não for uma string válida, retorna o que foi passado.
 * @param name Nome a ser formatado
 * @returns {string} Nome formatado
 */
export const formatFirstAndLastName = (name) => {
  if (typeof name === 'string') {
    const trimmedName = name.trim()

    if (trimmedName === '') {
      return ''
    }

    const words = trimmedName.split(' ').filter(Boolean)
    if (words.length > 1) {
      return `${words[0]} ${words[words.length - 1]}`
    } else if (words.length === 1) {
      return words[0]
    }
  }
  return name
}

/**
 * Trunca um texto mantendo um sufixo específico baseado em regex
 * Adiciona pontuação apropriada ao final do texto truncado
 * @param {Object} params - Parâmetros da função
 * @param {string} params.text - Texto a ser "truncado"
 * @param {number} params.maxLength - Comprimento máximo do texto desejado
 * @param {RegExp} [params.keepSuffixRegex] - Regex para identificar sufixo a ser mantido
 * @returns {string} Texto truncado com formatação adequada
 */
const extractTruncateSuffix = (text, keepSuffixRegex) => {
  if (!keepSuffixRegex || !keepSuffixRegex.test(text)) {
    return { suffix: '', processedText: text }
  }
  const match = text.match(keepSuffixRegex)
  if (!match) {
    return { suffix: '', processedText: text }
  }
  const suffix = match[0]
  const processedText = text.slice(0, -suffix.length).trimEnd()
  return { suffix, processedText }
}

const applyTruncateEllipsis = (truncatedText, processedText, maxLength) => {
  const nextChar = processedText[maxLength]
  const endsWithPunctuation =
    truncatedText.endsWith(',') || truncatedText.endsWith('.')
  const hasMoreText = nextChar !== undefined
  const shouldAddPeriod =
    !hasMoreText ||
    (nextChar === '.' && processedText[maxLength + 1] === undefined)

  if (shouldAddPeriod) {
    return truncatedText + '.'
  }
  if (endsWithPunctuation) {
    return truncatedText.slice(0, -1) + '...'
  }
  return truncatedText + '...'
}

const appendTruncateSuffix = (truncatedText, suffix) => {
  if (!suffix) {
    return truncatedText
  }
  const withSpace = truncatedText.endsWith(' ')
    ? truncatedText
    : truncatedText + ' '
  return withSpace + suffix
}

export const truncateText = ({ text, maxLength, keepSuffixRegex }) => {
  const { suffix, processedText } = extractTruncateSuffix(text, keepSuffixRegex)

  const isTextTruncated = processedText.length > maxLength
  if (!isTextTruncated) {
    return appendTruncateSuffix(processedText, suffix)
  }

  const truncatedText = processedText.slice(0, maxLength).trimEnd()
  return appendTruncateSuffix(
    applyTruncateEllipsis(truncatedText, processedText, maxLength),
    suffix
  )
}

export const convertDaysToTime = (days) => {
  if (days < 30) {
    return `${days} dia${days > 1 ? 's' : ''}`
  } else if (days < 365) {
    const months = Math.floor(days / 30)
    return months > 1 ? `${months} meses` : `${months} mês`
  } else {
    const years = Math.floor(days / 365)
    return `${years} ano${years > 1 ? 's' : ''}`
  }
}

/**
 * Tenta fazer o parse de uma data utilizando múltiplos formatos conhecidos.
 * Caso nenhum formato seja válido, retorna o fallback definido.
 *
 * @param {Object} params
 * @param {String|Date|moment} params.date - Valor da data a ser parseada.
 * @param {*} [params.fallback=null] - Valor retornado caso a data seja inválida.
 * @param {String|null} [params.outputFormat=null] - Se definido, retorna a data formatada neste padrão.
 * @returns {moment.Moment|String|*} Momento parseado, string formatada ou fallback.
 *
 * @example
 * parseDateSafe({ date: "25/01/2025" })
 * parseDateSafe({ date: "25-01-2025", outputFormat: "YYYY-MM-DD" })
 * parseDateSafe({ date: "data inválida", fallback: null })
 */
export const parseDateSafe = ({
  date,
  fallback = null,
  outputFormat = null,
}) => {
  // Guard: date falsy (null/undefined/''/0) cairia em aawzDate(date) ->
  // new Date(dateInput || Date.now()), retornando "hoje" em vez do fallback
  // documentado. Auditado (2026-08-07): todos os call sites do projeto ou já
  // guardam contra falsy antes de chamar parseDateSafe, ou têm fallback/
  // tratamento downstream para null que o bug estava mascarando.
  if (!date) return fallback

  // Ordem importa: formatos mais específicos (com segundos/hora) vêm antes dos
  // mais curtos para o round-trip check (`parsed.format(format) === normalized`)
  // casar o formato correto. Sem os variantes com `HH:mm:ss`, datas de corretora
  // como "01/07/2026 10:30:00" caíam no fallback `moment(date)` → JS Date() em
  // locale US → 07 de janeiro (MM/DD) → cor/contagem erradas.
  const formats = [
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY HH:mm',
    'DD-MM-YYYY',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD',
    'YYYY/MM/DD HH:mm:ss',
    'YYYY/MM/DD HH:mm',
    'YYYY/MM/DD',
    'DD/MMM/YYYY',
  ]

  const tryParse = () => {
    if (typeof date === 'string') {
      const normalized = date.trim().replace(/\s+/g, ' ')
      for (const format of formats) {
        const parsed = moment(normalized, format)
        if (parsed.isValid()) {
          if (parsed.format(format) === normalized) {
            return parsed
          }
        }
      }
    }

    const parsed = moment(date)
    return parsed.isValid() ? parsed : null
  }

  const parsedDate = tryParse()
  if (!parsedDate) return fallback

  return outputFormat ? parsedDate.format(outputFormat) : parsedDate
}

/**
 * Retorna a classe estilizada com base na data fornecida
 * @param {String} Date data a ser formatada
 * @param {String} defaultClass (opcional, para sobreescrever a classe default)
 * @param {String} additionalClass (opcional, para adicionar classes adicionais)
 * @param {Boolean} applyAdditionalToDefault (opcional, aplica (caso houver) a classe adicional quando cair em defaultClass)
 * @returns {String} classe estilizada correspondente.
 */
export const getStyleByDate = ({
  date,
  defaultClass = '',
  additionalClass = '',
  applyAdditionalToDefault = true,
}) => {
  if (!date) return defaultClass

  const parsedDate = parseDateSafe({ date })

  const getBaseComparison = () => {
    const today = moment().startOf('day')
    const target = parsedDate.startOf('day')
    const diffDays = target.diff(today, 'days')
    return { today, target, diffDays }
  }

  if (!parsedDate) return defaultClass

  const { today, target, diffDays } = getBaseComparison()

  if (!today.isValid() || !target.isValid()) return defaultClass

  let baseClass = defaultClass
  if (diffDays === 0) baseClass = 'text-warning-yellow'
  if (diffDays < 0) baseClass = 'text-red'

  const isDefault = baseClass === defaultClass
  if (isDefault && !applyAdditionalToDefault) return baseClass

  return `${baseClass} ${additionalClass}`.trim()
}

/**
 * Extrai o item principal do response da API.
 * @param {Object} response Response esperado com a chave value.
 * @returns {Object|undefined} Item do response (primeiro do array ou objeto).
 */
export const extractResponseValue = (response) => {
  if (!response || typeof response !== 'object') {
    return undefined
  }

  const raw = response.value
  if (Array.isArray(raw)) {
    return raw[0]
  }

  return raw
}

/**
 * Retorna o estilo para o chip de novo módulo.
 * @param {String} color Cor do chip.
 * @returns {Object} Estilo para o chip de novo módulo.
 */

export const getChipNewModuleProps = ({ color } = {}) => {
  const primary = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary')
    .trim()
  const baseColor = color || primary || '#000000'
  const textColor = baseColor ? textOnColor(baseColor) : '#000000'

  return {
    xSmall: true,
    color: baseColor,
    style: {
      color: textColor,
      height: '19px',
      borderRadius: '16px',
      marginLeft: '8px',
    },
  }
}

const UNIDADES_TAMANHO_ARQUIVO = ['B', 'KB', 'MB', 'GB', 'TB']
const BYTES_POR_UNIDADE = 1024
const CASAS_DECIMAIS_TAMANHO = 1

/**
 * Formata bytes na maior unidade que ainda deixa o número legível.
 *
 * Bytes saem sem casa decimal ("512 B"); as demais com uma ("1.5 KB").
 *
 * ★ O KB passou a ter casa decimal em 25/08/2026, unificando com a
 * implementação que o chat de IA tinha criado à parte: eram duas funções para
 * o mesmo problema, e 1536 bytes viravam "2 KB" numa tela e "1.5 KB" na outra.
 * O laço também trouxe GB/TB, que a versão anterior não cobria — arquivo de
 * 2 GB aparecia como "2048.0 MB".
 *
 * @param {Number} bytes Tamanho em bytes (`File.size`).
 * @returns {String} Ex.: `'0 B'`, `'512 B'`, `'1.5 KB'`.
 */
export const formatFileSize = (bytes) => {
  const valor = Number(bytes)
  // NaN, null, 0 e negativo caem no mesmo lugar: "0 B" é honesto e não deixa
  // "NaN undefined" chegar à tela.
  if (!valor || valor < 0) return '0 ' + UNIDADES_TAMANHO_ARQUIVO[0]
  const ultima = UNIDADES_TAMANHO_ARQUIVO.length - 1
  let restante = valor
  let unidade = 0
  while (restante >= BYTES_POR_UNIDADE && unidade < ultima) {
    restante = restante / BYTES_POR_UNIDADE
    unidade = unidade + 1
  }
  const casas = unidade === 0 ? 0 : CASAS_DECIMAIS_TAMANHO
  return restante.toFixed(casas) + ' ' + UNIDADES_TAMANHO_ARQUIVO[unidade]
}

/**
 * Formata a data "pago até" (ISO YYYY-MM-DD) para mês/ano (ex.: mai/2026).
 * @param {String} date Data ISO.
 * @returns {String} Mês/ano abreviado, ou '' quando ausente/invalida.
 */
export const toPagoAte = (date) => {
  if (!date) return ''
  const parts = String(date).split('-')
  if (parts.length < 2) return ''
  return toMonthYear(`${parts[1]}/${parts[0]}`, false, true)
}

/**
 * Capitaliza a primeira letra, preservando o resto da string.
 *
 * Existia copiada em 4 componentes do módulo ai-framework (approval-queue-item,
 * proposed-action-card, run-timeline e approval-reject) — todas idênticas.
 * @param {String} value Texto de entrada.
 * @returns {String} Texto com a inicial maiúscula, ou '' quando vazio/ausente.
 */
export const capitalize = (value = '') => {
  const text = value || ''
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}
